import { useAdmin, usePermissions } from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { useSalaryModuleConfiguration } from './hooks/useSalaryModuleConfiguration'

interface ApprovableWorkPeriod {
  user: { id: string }
  approvedByUser: boolean
  userComment: string
  approvedByManager: boolean
  managerComment: string
}

export const useWorkPeriodApprovePermissions = () => {
  const { hasPermissions } = usePermissions()
  const admin = useAdmin()
  const salaryModuleConfiguration = useSalaryModuleConfiguration()
  const canConfirmOthers = hasPermissions(
    [
      PERMISSIONS.workPeriod.view.othersworkperiod,
      PERMISSIONS.workPeriod.approve.workperiod,
      PERMISSIONS.workPeriod.change.workperiod,
    ],
    true
  )

  function workPeriodApprovalState(
    meId: string,
    workPeriod: ApprovableWorkPeriod
  ) {
    const isManager = meId !== workPeriod.user.id && (canConfirmOthers || admin)
    const driverApproveDirty =
      workPeriod.approvedByUser || workPeriod.userComment
    const managerApproveDirty =
      workPeriod.approvedByManager || workPeriod.managerComment

    const canApproveAsDriver =
      (meId === workPeriod.user.id || admin) &&
      !(
        workPeriod.approvedByManager ||
        !salaryModuleConfiguration.managerApprove
      )
    // When a company requires both the driver confirmation and the manager
    // approval steps, a manager cannot approve a work period the driver has not
    // yet confirmed. This mirrors the backend guard (which allows no admin
    // bypass) and only blocks the approve action, never un-approving an already
    // approved period.
    const managerBlockedByDriverConfirmation =
      isManager &&
      salaryModuleConfiguration.driverApprove &&
      salaryModuleConfiguration.managerApprove &&
      !workPeriod.approvedByUser &&
      !workPeriod.approvedByManager

    const canApproveAsManager =
      isManager &&
      // A manager can always undo their own approval, so an already
      // manager-approved period stays toggleable regardless of driver
      // confirmation (matches the backend, which only guards the approve step).
      (workPeriod.approvedByUser ||
        !salaryModuleConfiguration.driverApprove ||
        admin ||
        workPeriod.approvedByManager) &&
      !managerBlockedByDriverConfirmation

    return {
      driverApproveDirty,
      managerApproveDirty,
      canApproveAsDriver,
      canApproveAsManager,
      managerBlockedByDriverConfirmation,
    }
  }

  return { canConfirmOthers, workPeriodApprovalState }
}
